import { Controller } from '@hotwired/stimulus';

const FIELD_TYPE_DEFAULT = 'default';
const FIELD_TYPE_TIER = 'tier';

export default class extends Controller {
  static targets = ['fieldType', 'selectorColumn', 'toggleButton', 'fieldColumn']

  static values = {
    fieldName: String,
    currentValue: String,
    currentFieldType: String,
    currentFieldConfig: Object,
    ediElementId: String
  }

  connect() {
    this.selectorVisible = this.currentFieldTypeValue !== FIELD_TYPE_DEFAULT;
    this.abortController = null;
    this.debounceTimer = null;
    this.initializeExistingPairs();
    this.updateSelectorVisibility();

    this.boundHideSelector = this.hideSelector.bind(this);
    this.boundShowSelectorIfNeeded = this.showSelectorIfNeeded.bind(this);

    const turboFrame = this.#getTurboFrame();
    if (turboFrame) {
      turboFrame.addEventListener('turbo:before-frame-render', this.boundHideSelector);
      turboFrame.addEventListener('turbo:frame-render', this.boundShowSelectorIfNeeded);
    }
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    const turboFrame = this.#getTurboFrame();
    if (turboFrame) {
      turboFrame.removeEventListener('turbo:before-frame-render', this.boundHideSelector);
      turboFrame.removeEventListener('turbo:frame-render', this.boundShowSelectorIfNeeded);
    }
  }

  updateField() {
    this.updateFieldWithType(this.fieldTypeTarget.value);
  }

  toggleSelector() {
    this.selectorVisible = !this.selectorVisible;
    const firstOption = this.fieldTypeTarget.options[0]?.value || FIELD_TYPE_TIER;
    const type = this.selectorVisible ? firstOption : FIELD_TYPE_DEFAULT;
    this.fieldTypeTarget.value = type;

    this.updateSelectorVisibility();
    this.updateFieldWithType(type);
  }

  updateSelectorVisibility() {
    const type = this.getCurrentFieldType();
    const showSelector = type !== FIELD_TYPE_DEFAULT && this.selectorVisible;
    this.#setSelectorColumnVisibility(showSelector);
  }

  hideSelector() {
    this.#setSelectorColumnVisibility(false);
  }

  showSelectorIfNeeded() {
    const turboFrame = this.#getTurboFrame();
    if (turboFrame) turboFrame.style.display = 'block';

    if (this.pendingFieldType && this.pendingFieldType !== FIELD_TYPE_DEFAULT) {
      this.fieldTypeTarget.value = this.pendingFieldType;
    }
    this.pendingFieldType = null;

    this.initializeExistingPairs();
    this.updateSelectorVisibility();
  }

  getCurrentFieldType() {
    return this.fieldTypeTarget?.value || this.currentFieldTypeValue;
  }

  updateFieldWithType(fieldType) {
    // Cancel previous request if still pending
    if (this.abortController) {
      this.abortController.abort();
    }

    // Debounce rapid toggles
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      const turboFrame = this.#getTurboFrame();
      if (!turboFrame) return;

      turboFrame.style.display = 'none';

      this.pendingFieldType = fieldType;
      this.abortController = new AbortController();

      turboFrame.src = this.#buildFieldUrl(turboFrame, fieldType);
    }, 150);
  }

  addPair() {
    const container = this.element.querySelector('.pairs-container');
    if (!container) return;

    const newIndex = container.querySelectorAll('.pair-row').length;
    const url = this.#buildAddPairUrl(this.getCurrentFieldType(), newIndex);

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const newRow = tempDiv.firstElementChild;
        container.appendChild(newRow);
        this.initializePair(newRow);
      });
  }

  removePair(event) {
    const row = event.target.closest('.pair-row');
    if (row) row.remove();
  }

  updatePairName(event) {
    const row = event.target.closest('.pair-row');
    if (row) this.#updatePairInputs(row, true);
  }

  initializePair(row) {
    this.#updatePairInputs(row, false);
  }

  #updatePairInputs(row, clearValue = false) {
    const inputs = row.querySelectorAll("input[type='text']");
    if (inputs.length < 2) return;

    const [name, value] = inputs;
    const key = name?.value.trim();

    if (key && value) {
      value.name = `${this.fieldNameValue}[field_config][${key}]`;
      value.disabled = false;
    } else if (value) {
      value.removeAttribute('name');
      value.disabled = true;
      if (clearValue) value.value = '';
    }
  }

  initializeExistingPairs() {
    const container = this.element.querySelector('.pairs-container');
    if (!container) return;

    container.querySelectorAll('.pair-row').forEach((row) => this.initializePair(row));
  }

  #getTurboFrame() {
    return this.element.querySelector('turbo-frame');
  }

  #getBaseUrl() {
    const frame = this.#getTurboFrame();
    const src = frame?.getAttribute('src') || frame?.getAttribute('data-url');
    return src?.split('/render_field')[0] || '';
  }

  #buildAddPairUrl(type, index) {
    const url = new URL(`${this.#getBaseUrl()}/add_pair`, window.location.origin);
    url.searchParams.set('field_name', this.fieldNameValue);
    url.searchParams.set('field_type', type);
    url.searchParams.set('pair_index', index);
    return url.toString();
  }

  #buildFieldUrl(frame, type) {
    const baseUrl = frame.getAttribute('src') || frame.getAttribute('data-url');
    const url = new URL(baseUrl.split('?')[0], window.location.origin);

    const isSameType = type === this.currentFieldTypeValue;
    const fieldConfig = isSameType && this.currentFieldConfigValue && Object.keys(this.currentFieldConfigValue).length > 0
      ? JSON.stringify(this.currentFieldConfigValue)
      : '{}';

    url.searchParams.set('edi_element_id', this.ediElementIdValue);
    url.searchParams.set('field_name', this.fieldNameValue);
    url.searchParams.set('field_type', type);
    url.searchParams.set('field_config', fieldConfig);
    url.searchParams.set('current_value', this.currentValueValue);
    return url.toString();
  }

  #setSelectorColumnVisibility(visible) {
    this.selectorColumnTarget.classList.toggle('d-none', !visible);
    this.fieldColumnTarget.classList.remove('col-6', 'col-11');
    this.fieldColumnTarget.classList.add(visible ? 'col-6' : 'col-11');
  }
}
